import { useCallback, useEffect, useState } from 'react';
import { Card, ConfirmDialog, EmptyState, FormField, FormSelect, Modal, TextInput } from '@/components/ui';
import { superApi, type VoiceKbDocument, type VoiceKbView } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * טאב "מאגר ידע" בסטודיו הסוכן: צפייה במסמכי ה-RAG שהסוכן שולף מהם
 * (search_knowledge), הוספה/עריכה/מחיקה, הטמעה-מחדש, וחיפוש בדיקה שמראה
 * בדיוק מה הסוכן היה מוצא. מחיקה מנקה את וקטורי Qdrant בצד השרת.
 */

const STATUS_LABEL: Record<string, string> = { ready: 'מוכן', processing: 'מעבד…', failed: 'נכשל' };

export function StudioKnowledgeTab({ agentId, knowledgeBaseId }: { agentId: number; knowledgeBaseId?: number | null }) {
  const [view, setView] = useState<VoiceKbView | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<VoiceKbDocument | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<VoiceKbDocument | null>(null);
  const [query, setQuery] = useState('');
  const [searchResult, setSearchResult] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await superApi.voiceKb(agentId));
    } catch {
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { void load(); }, [load]);

  if (!knowledgeBaseId) {
    return (
      <EmptyState
        icon="📚"
        title="אין מאגר ידע מחובר"
        description="חבר מאגר ידע לסוכן בטופס העריכה (שדה 'מאגר ידע (RAG)'). לאחר מכן תוכל לנהל כאן את המסמכים שהסוכן עונה מתוכם."
      />
    );
  }

  return (
    <div className="stack">
      <Card
        title={view ? `מסמכים במאגר "${view.knowledge_base.name}" (${view.documents.length})` : 'מסמכים'}
        action={<button type="button" className="btn primary" onClick={() => setAddOpen(true)}>+ מסמך</button>}
      >
        {loading ? (
          <p style={{ color: 'var(--muted)' }}>טוען…</p>
        ) : !view || view.documents.length === 0 ? (
          <EmptyState icon="📄" title="אין מסמכים" description="הוסף מסמך טקסט (תפריט, מדיניות, שאלות נפוצות) שהסוכן ישלוף ממנו בשיחה." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 'var(--t-sm)' }}>
              <thead>
                <tr><th>כותרת</th><th>סוג</th><th>סטטוס</th><th>קטעים</th><th aria-label="פעולות" /></tr>
              </thead>
              <tbody>
                {view.documents.map((d) => (
                  <tr key={d.id}>
                    <td>{d.title}</td>
                    <td style={{ color: 'var(--muted)' }}>{d.type === 'url' ? 'קישור' : 'טקסט'}</td>
                    <td style={{ color: d.status === 'failed' ? 'var(--danger, #ef4444)' : d.status === 'ready' ? 'var(--ok, #22c55e)' : 'var(--muted)' }}>
                      {STATUS_LABEL[d.status] ?? d.status}
                    </td>
                    <td style={{ color: 'var(--muted)' }}>{d.chunk_count ?? 0}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn ghost" onClick={async () => setEditDoc(await superApi.voiceKbDocument(agentId, d.id))}>עריכה</button>
                      <button type="button" className="btn ghost" onClick={async () => {
                        try { await superApi.reprocessVoiceKbDocument(agentId, d.id); toast('המסמך עובד מחדש', 'ok'); void load(); }
                        catch { toast('העיבוד נכשל', 'danger'); }
                      }}>עבד מחדש</button>
                      <button type="button" className="btn ghost" onClick={() => setDeleteDoc(d)}>מחיקה</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="חיפוש בדיקה" >
        <p style={{ margin: '0 0 8px', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          מה שתמצא כאן הוא בדיוק מה שהסוכן ישלוף מהמאגר לשאלה הזו בשיחה.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="לדוגמה: כמה עולה משפחתית?" style={{ flex: 1 }} />
          <button
            type="button" className="btn" disabled={searching || !query.trim()}
            onClick={async () => {
              setSearching(true);
              try {
                const r = await superApi.voiceKbSearch(agentId, query.trim());
                const ctx = (r as { context?: string })?.context;
                setSearchResult(ctx ?? JSON.stringify(r, null, 2));
              } catch { toast('החיפוש נכשל', 'danger'); }
              finally { setSearching(false); }
            }}
          >{searching ? 'מחפש…' : 'חפש'}</button>
        </div>
        {searchResult !== null && (
          <pre style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 'var(--t-sm)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, maxHeight: 280, overflowY: 'auto' }}>{searchResult}</pre>
        )}
      </Card>

      {addOpen && <DocModal agentId={agentId} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); void load(); }} />}
      {editDoc && <DocModal agentId={agentId} doc={editDoc} onClose={() => setEditDoc(null)} onSaved={() => { setEditDoc(null); void load(); }} />}

      <ConfirmDialog
        isOpen={!!deleteDoc}
        title="מחיקת מסמך"
        message={`למחוק את "${deleteDoc?.title}"? התוכן יוסר גם מחיפוש הסוכן (Qdrant).`}
        onConfirm={async () => {
          if (!deleteDoc) return;
          try { await superApi.deleteVoiceKbDocument(agentId, deleteDoc.id); toast('המסמך נמחק', 'ok'); setDeleteDoc(null); void load(); }
          catch { toast('המחיקה נכשלה', 'danger'); }
        }}
        onClose={() => setDeleteDoc(null)}
      />
    </div>
  );
}

function DocModal({ agentId, doc, onClose, onSaved }: { agentId: number; doc?: VoiceKbDocument; onClose: () => void; onSaved: () => void }) {
  const editing = !!doc;
  const [type, setType] = useState<'text' | 'url'>((doc?.type as 'text' | 'url') ?? 'text');
  const [title, setTitle] = useState(doc?.title ?? '');
  const [content, setContent] = useState(doc?.content ?? '');
  const [url, setUrl] = useState(doc?.url ?? '');
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      title={editing ? `עריכת "${doc!.title}"` : 'מסמך חדש'}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button
            type="button" className="btn primary"
            disabled={saving || !title.trim() || (type === 'text' ? !content.trim() : !url.trim())}
            onClick={async () => {
              setSaving(true);
              try {
                if (editing) {
                  await superApi.updateVoiceKbDocument(agentId, doc!.id, { title: title.trim(), ...(type === 'text' ? { content } : { url: url || undefined }) });
                } else {
                  await superApi.addVoiceKbDocument(agentId, { type, title: title.trim(), ...(type === 'text' ? { content } : { url }) });
                }
                toast('נשמר', 'ok');
                onSaved();
              } catch (e: unknown) {
                toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'השמירה נכשלה', 'danger');
              } finally { setSaving(false); }
            }}
          >{saving ? 'שומר…' : 'שמור'}</button>
        </>
      }
    >
      <div className="stack">
        {!editing && (
          <FormField label="סוג">
            <FormSelect value={type} onChange={(e) => setType(e.target.value as 'text' | 'url')}>
              <option value="text">טקסט</option>
              <option value="url">קישור (URL)</option>
            </FormSelect>
          </FormField>
        )}
        <FormField label="כותרת">
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </FormField>
        {type === 'text' ? (
          <FormField label="תוכן" hint="הטקסט שהסוכן ישלוף ממנו. שינוי תוכן מטמיע מחדש אוטומטית.">
            <textarea className="input" rows={10} value={content} onChange={(e) => setContent(e.target.value)} />
          </FormField>
        ) : (
          <FormField label="כתובת URL" hint="הטקסט יישלף מהעמוד אוטומטית">
            <TextInput value={url ?? ''} dir="ltr" onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </FormField>
        )}
      </div>
    </Modal>
  );
}
